import { useEffect, useState, FormEvent } from "react";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.js";

interface Scenario {
  id: string; name: string; kind: string; city_id?: string;
  status: string; season_tags_json?: string[]; created_at: string;
}

export function ScenariosPage() {
  const { role } = useAuth();
  const [list, setList] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "generic", status: "active" });
  const [formError, setFormError] = useState("");

  function load() {
    setLoading(true);
    api.get<{ scenarios: Scenario[] }>("/scenarios?limit=50")
      .then(r => setList(r.scenarios ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setFormError("");
    try {
      await api.post("/scenarios", form);
      setCreating(false);
      setForm({ name: "", kind: "generic", status: "active" });
      load();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setFormError(e.data?.message || "Error al crear escenario");
    }
  }

  const kindLabel = (k: string) => ({ generic: "Genérico", exact_place: "Lugar exacto", private_fictional: "Privado/Ficticio" }[k] || k);
  const statusColor = (s: string) => s === "active" ? "#2d6a4f" : s === "archived" ? "#9ca3af" : "#d97706";

  if (loading) return <div style={{ padding: 24, color: "#888" }}>Cargando…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Escenarios</h1>
          <p style={{ color: "#666", fontSize: 13, margin: "4px 0 0" }}>{list.length} localizaciones</p>
        </div>
        {(role === "owner" || role === "editor") && (
          <button onClick={() => setCreating(!creating)} style={btnPrim}>+ Nuevo escenario</button>
        )}
      </div>

      {creating && (
        <form onSubmit={handleCreate} style={{ background: "#fff", padding: 24, borderRadius: 10, marginBottom: 24, display: "flex", flexDirection: "column", gap: 12, maxWidth: 480 }}>
          <h3 style={{ margin: 0 }}>Nuevo escenario</h3>
          {formError && <div style={{ color: "#dc2626", fontSize: 13 }}>{formError}</div>}
          <label style={lbl}>Nombre <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={inp} placeholder="Playa mediterránea, tarde…" /></label>
          <label style={lbl}>Tipo <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} style={inp}>
            <option value="generic">Genérico</option>
            <option value="exact_place">Lugar exacto (requiere evidencia)</option>
            <option value="private_fictional">Privado / Ficticio</option>
          </select></label>
          {form.kind === "exact_place" && (
            <div style={{ background: "#fef9c3", padding: "8px 12px", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
              Los lugares exactos requieren fuentes y fecha de evidencia para aprobarse.
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" style={btnPrim}>Crear</button>
            <button type="button" onClick={() => setCreating(false)} style={btnSec}>Cancelar</button>
          </div>
        </form>
      )}

      {list.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 10, padding: 40, textAlign: "center", color: "#888" }}>
          Sin escenarios. Crea localizaciones para usarlas en sesiones de fotos.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {list.map(s => (
            <div key={s.id} style={{ background: "#fff", borderRadius: 10, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{s.name}</div>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: statusColor(s.status) + "22", color: statusColor(s.status), fontWeight: 600 }}>{s.status}</span>
              </div>
              <div style={{ fontSize: 12, color: "#888" }}>{kindLabel(s.kind)}</div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>{new Date(s.created_at).toLocaleDateString("es")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const btnPrim: React.CSSProperties = { padding: "8px 16px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const btnSec: React.CSSProperties = { padding: "8px 16px", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", background: "none", fontSize: 13 };
const inp: React.CSSProperties = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
const lbl: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#444" };
